import { uniqueById } from '@repo/attendances-core'
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@repo/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { LayoutGridIcon, ListIcon, SlidersHorizontalIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { BulkAttendanceActionDialog } from '@/components/inbox/BulkAttendanceActionDialog'
import { BulkFinalizationDialog } from '@/components/inbox/BulkFinalizationDialog'
import { FinalizationDialog } from '@/components/inbox/FinalizationDialog'
import { AttendanceBoard } from '@/features/attendance-board/components/attendance-board'
import { ColumnManagerDialog } from '@/features/attendance-board/components/column-manager-dialog'
import { InboxChat } from '@/features/inbox-main/components/inbox-chat'
import { InboxDetails } from '@/features/inbox-main/components/inbox-details'
import { InboxSidebar } from '@/features/inbox-main/components/inbox-sidebar'
import {
  useArchiveAttendance,
  useResolveBulkAttendanceSelection,
  useTransferAttendance,
} from '@/hooks/use-attendance-actions'
import {
  useAttendance,
  useAttendanceCampaignOptions,
  useAttendances,
} from '@/hooks/use-attendances'
import { useAuth } from '@/hooks/use-auth'
import { useChannels } from '@/hooks/use-channels'
import { useInboxMembers } from '@/hooks/use-inbox-members'
import { useInboxSocket } from '@/hooks/use-inbox-socket'
import { useTags } from '@/hooks/use-leads'
import { useMessageSound } from '@/hooks/use-message-sound'
import { useMessages } from '@/hooks/use-messages'
import { useDepartments } from '@/hooks/use-settings'
import type { AttendanceFilters } from '@/types/attendances'

type InboxSearch = {
  id?: string
  channelId?: string
  status?: string
  mode?: string
  assignee?: string
  unreadOnly?: boolean
  departmentId?: string
  tagId?: string
  campaignId?: string
  interaction?: string
  windowState?: string
  sort?: 'priority' | 'recent'
  search?: string
  view?: 'kanban'
}

export const Route = createFileRoute('/_app/inbox')({
  validateSearch: (raw: Record<string, unknown>): InboxSearch => ({
    id: typeof raw.id === 'string' ? raw.id : undefined,
    channelId: typeof raw.channelId === 'string' ? raw.channelId : undefined,
    status: typeof raw.status === 'string' ? raw.status : undefined,
    mode: typeof raw.mode === 'string' ? raw.mode : undefined,
    assignee: typeof raw.assignee === 'string' ? raw.assignee : undefined,
    unreadOnly: raw.unreadOnly === true || raw.unreadOnly === 'true',
    departmentId:
      typeof raw.departmentId === 'string' ? raw.departmentId : undefined,
    tagId: typeof raw.tagId === 'string' ? raw.tagId : undefined,
    campaignId: typeof raw.campaignId === 'string' ? raw.campaignId : undefined,
    interaction:
      typeof raw.interaction === 'string' ? raw.interaction : undefined,
    windowState:
      typeof raw.windowState === 'string' ? raw.windowState : undefined,
    sort: raw.sort === 'recent' ? 'recent' : undefined,
    search: typeof raw.search === 'string' ? raw.search : undefined,
    view: raw.view === 'kanban' ? 'kanban' : undefined,
  }),
  component: InboxPage,
})

const DEFAULT_FILTERS: AttendanceFilters = {
  status: 'OPEN',
  mode: 'all',
  assignedTo: 'all',
  departmentId: 'all',
  channelId: 'all',
  tagId: 'all',
  campaignId: 'all',
  unreadOnly: false,
  interaction: 'all',
  windowState: 'all',
  sort: 'priority',
  search: '',
}

const MAX_BULK_SELECTION = 2000

function InboxPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { currentOrgId, currentRole, user } = useAuth()
  const { play: playSound } = useMessageSound()

  const selectedId = search.id ?? null

  // Ao trocar de org ativa, o `?id` (e os filtros por entidade) pertenciam à org
  // anterior. Detectamos a troca já na renderização e zeramos `activeSelectedId`
  // AGORA — impedindo useAttendance/useMessages de consultarem
  // `(orgNova, conversaAntiga)` e dispararem 404 antes do efeito limpar a URL.
  const prevOrgIdRef = useRef(currentOrgId)
  const orgJustChanged =
    prevOrgIdRef.current !== null && prevOrgIdRef.current !== currentOrgId
  const activeSelectedId = orgJustChanged ? null : selectedId

  useEffect(() => {
    const prev = prevOrgIdRef.current
    prevOrgIdRef.current = currentOrgId
    if (prev === null || currentOrgId === null || prev === currentOrgId) return
    // Troca real de org: limpa a conversa e os filtros scoped da org antiga.
    void navigate({
      search: (prevSearch) => ({
        ...prevSearch,
        id: undefined,
        channelId: undefined,
        departmentId: undefined,
        tagId: undefined,
        campaignId: undefined,
        assignee: undefined,
      }),
    })
  }, [currentOrgId, navigate])

  const [finalizationOpen, setFinalizationOpen] = useState(false)
  const [transferOpen, setTransferOpen] = useState(false)
  const [transferUserId, setTransferUserId] = useState<string>('none')
  const [transferDeptId, setTransferDeptId] = useState<string>('')
  const [detailsOpen, setDetailsOpen] = useState(true)
  const [manageBoardOpen, setManageBoardOpen] = useState(false)
  const [bulkFinalizationOpen, setBulkFinalizationOpen] = useState(false)
  const [bulkActionOpen, setBulkActionOpen] = useState(false)
  const [allMatchingSelected, setAllMatchingSelected] = useState(false)
  const [selectedAttendanceIds, setSelectedAttendanceIds] = useState<
    Set<string>
  >(new Set())

  const { data: members = [] } = useInboxMembers(currentOrgId)
  const { data: channels = [] } = useChannels(currentOrgId)
  const { data: departments = [] } = useDepartments(currentOrgId)
  const { data: tags = [] } = useTags(currentOrgId)
  const { data: campaigns = [] } = useAttendanceCampaignOptions(currentOrgId)
  const resolveBulkSelection = useResolveBulkAttendanceSelection(currentOrgId)

  const filters: AttendanceFilters = {
    ...DEFAULT_FILTERS,
    channelId: search.channelId ?? 'all',
    status: (search.status as AttendanceFilters['status']) ?? 'OPEN',
    mode: (search.mode as AttendanceFilters['mode']) ?? 'all',
    assignedTo:
      search.assignee === 'me'
        ? (user?.id ?? 'all')
        : (search.assignee ?? 'all'),
    departmentId: search.departmentId ?? 'all',
    tagId: search.tagId ?? 'all',
    campaignId: search.campaignId ?? 'all',
    unreadOnly: search.unreadOnly ?? false,
    interaction:
      (search.interaction as AttendanceFilters['interaction']) ?? 'all',
    windowState:
      (search.windowState as AttendanceFilters['windowState']) ?? 'all',
    sort: search.sort ?? 'priority',
    search: search.search ?? '',
  }

  const {
    data,
    isLoading: listLoading,
    isError: listError,
    refetch: refetchList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAttendances(currentOrgId, filters)
  const attendances = uniqueById(data?.pages.flatMap((p) => p.data) ?? [])
  const attendanceLabels = new Map(
    attendances.map((attendance) => [
      attendance.id,
      attendance.lead?.name ?? attendance.lead?.phone ?? 'Sem nome',
    ])
  )
  const totalCount = data?.pages[0]?.total
  const channelCounts = data?.pages[0]?.byChannel ?? []
  const modeCounts = data?.pages[0]?.modeCounts ?? { pending: 0, ai: 0 }
  const interactionCounts = data?.pages[0]?.interactionCounts ?? {
    responded: 0,
    waitingHuman: 0,
    inService: 0,
    noResponse: 0,
  }

  const { data: selectedAttendance, isLoading: selectedLoading } =
    useAttendance(currentOrgId, activeSelectedId)
  const {
    data: messagesData,
    isLoading: messagesLoading,
    fetchNextPage: fetchOlderMessages,
    hasNextPage: hasOlderMessages,
    isFetchingNextPage: isFetchingOlderMessages,
  } = useMessages(currentOrgId, activeSelectedId)
  // Pages are newest-first; reverse so oldest page renders at top
  const messages = [...(messagesData?.pages ?? [])]
    .reverse()
    .flatMap((p) => [...p.data].reverse())

  const transferMutation = useTransferAttendance(currentOrgId, activeSelectedId)
  const archiveMutation = useArchiveAttendance(currentOrgId, activeSelectedId)

  useInboxSocket(currentOrgId, {
    onMessageNew: (message, conversationId) => {
      if (message.direction === 'inbound' && conversationId !== selectedId)
        playSound()
    },
  })

  function handleFiltersChange(partial: {
    channelId?: string
    status?: string
    mode?: string
    assignee?: string
    unreadOnly?: boolean
    departmentId?: string
    tagId?: string
    campaignId?: string
    interaction?: string
    windowState?: string
    sort?: 'priority' | 'recent'
    search?: string
  }) {
    setSelectedAttendanceIds(new Set())
    setAllMatchingSelected(false)
    void navigate({
      search: (prev) => ({
        ...prev,
        channelId:
          'channelId' in partial
            ? partial.channelId === 'all'
              ? undefined
              : partial.channelId
            : prev.channelId,
        status:
          'status' in partial
            ? partial.status === 'OPEN'
              ? undefined
              : partial.status
            : prev.status,
        mode:
          'mode' in partial
            ? partial.mode === 'all'
              ? undefined
              : partial.mode
            : prev.mode,
        assignee:
          'assignee' in partial
            ? partial.assignee === 'all'
              ? undefined
              : partial.assignee
            : prev.assignee,
        unreadOnly:
          'unreadOnly' in partial
            ? partial.unreadOnly || undefined
            : prev.unreadOnly,
        departmentId:
          'departmentId' in partial
            ? partial.departmentId === 'all'
              ? undefined
              : partial.departmentId
            : prev.departmentId,
        tagId:
          'tagId' in partial
            ? partial.tagId === 'all'
              ? undefined
              : partial.tagId
            : prev.tagId,
        campaignId:
          'campaignId' in partial
            ? partial.campaignId === 'all'
              ? undefined
              : partial.campaignId
            : prev.campaignId,
        interaction:
          'interaction' in partial
            ? partial.interaction === 'all'
              ? undefined
              : partial.interaction
            : prev.interaction,
        windowState:
          'windowState' in partial
            ? partial.windowState === 'all'
              ? undefined
              : partial.windowState
            : prev.windowState,
        sort:
          'sort' in partial
            ? partial.sort === 'priority'
              ? undefined
              : partial.sort
            : prev.sort,
        search:
          'search' in partial
            ? partial.search?.trim() || undefined
            : prev.search,
      }),
      replace: true,
    })
  }

  function handleAttendanceSelection(attendanceId: string, checked: boolean) {
    setAllMatchingSelected(false)
    setSelectedAttendanceIds((current) => {
      const next = new Set(current)
      if (!checked) {
        next.delete(attendanceId)
        return next
      }
      if (next.size >= MAX_BULK_SELECTION) {
        toast.warning(
          `É possível finalizar até ${MAX_BULK_SELECTION} atendimentos por vez.`
        )
        return current
      }
      next.add(attendanceId)
      return next
    })
  }

  function handleAllLoadedSelection(attendanceIds: string[], checked: boolean) {
    setAllMatchingSelected(false)
    if (!checked) {
      setSelectedAttendanceIds(new Set())
      return
    }

    const limitedIds = attendanceIds.slice(0, MAX_BULK_SELECTION)
    setSelectedAttendanceIds(new Set(limitedIds))
    if (attendanceIds.length > MAX_BULK_SELECTION) {
      toast.warning(
        `Os primeiros ${MAX_BULK_SELECTION} atendimentos carregados foram selecionados.`
      )
    }
  }

  async function handleSelectAllMatching() {
    const result = await resolveBulkSelection.mutateAsync(filters)
    setSelectedAttendanceIds(new Set(result.attendanceIds))
    setAllMatchingSelected(!result.truncated)
    if (result.truncated) {
      toast.warning(
        `A seleção foi limitada aos primeiros ${result.limit} de ${result.total} atendimentos.`
      )
    } else {
      toast.success(
        `${result.attendanceIds.length} atendimentos filtrados selecionados.`
      )
    }
  }

  function openTransfer() {
    setTransferDeptId(selectedAttendance?.department?.id ?? '')
    setTransferUserId(selectedAttendance?.assignedUserId ?? 'none')
    setTransferOpen(true)
  }

  // Membros elegíveis como responsável no setor de destino escolhido.
  const transferEligibleMembers = useMemo(
    () =>
      transferDeptId
        ? members.filter((m) => m.departmentIds.includes(transferDeptId))
        : members,
    [members, transferDeptId]
  )

  async function handleTransferConfirm() {
    await transferMutation.mutateAsync({
      departmentId: transferDeptId || undefined,
      assignedUserId: transferUserId === 'none' ? null : transferUserId,
    })
    setTransferOpen(false)
    setTransferUserId('none')
    setTransferDeptId('')
  }

  if (!currentOrgId) return null

  function openBoard() {
    void navigate({
      search: (prev) => ({ ...prev, id: undefined, view: 'kanban' }),
      replace: false,
    })
  }

  function openConversations() {
    void navigate({
      search: (prev) => ({ ...prev, view: undefined }),
      replace: false,
    })
  }

  if (search.view === 'kanban') {
    return (
      <div className="flex h-[calc(100svh-3.5rem)] min-h-0 flex-col overflow-hidden">
        <header className="bg-card flex shrink-0 flex-wrap items-center justify-between gap-3 border-b px-4 py-3 md:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
              <LayoutGridIcon className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold md:text-lg">
                Kanban de atendimentos
              </h1>
              <p className="text-muted-foreground truncate text-xs md:text-sm">
                Acompanhe a fila e mova atendimentos entre etapas
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button type="button" variant="outline" onClick={openConversations}>
              <ListIcon className="h-4 w-4" />
              Conversas
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setManageBoardOpen(true)}
            >
              <SlidersHorizontalIcon className="h-4 w-4" />
              Gerenciar colunas
            </Button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-hidden">
          <AttendanceBoard orgId={currentOrgId} />
        </div>

        <ColumnManagerDialog
          orgId={currentOrgId}
          open={manageBoardOpen}
          onOpenChange={setManageBoardOpen}
        />
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100svh-3.5rem)] overflow-hidden">
      {/* Sidebar */}
      <aside
        className={cn(
          'min-h-0 shrink-0',
          selectedId
            ? 'hidden md:flex md:w-80 md:flex-col'
            : 'flex w-full flex-col md:w-80'
        )}
      >
        <InboxSidebar
          attendances={attendances}
          selectedId={selectedId}
          currentUserId={user?.id ?? ''}
          onSelect={(id) =>
            void navigate({
              search: (prev) => ({ ...prev, id }),
              replace: false,
            })
          }
          onFilterChange={(partial) => handleFiltersChange(partial)}
          isLoading={listLoading}
          hasError={listError}
          onRetry={() => void refetchList()}
          members={members}
          channels={channels}
          departments={departments}
          tags={tags}
          campaigns={campaigns}
          orgId={currentOrgId}
          filters={{
            channelId: search.channelId ?? 'all',
            status: search.status ?? 'OPEN',
            mode: search.mode ?? 'all',
            assignee: search.assignee ?? 'all',
            unreadOnly: search.unreadOnly ?? false,
            departmentId: search.departmentId ?? 'all',
            tagId: search.tagId ?? 'all',
            campaignId: search.campaignId ?? 'all',
            interaction: search.interaction ?? 'all',
            windowState: search.windowState ?? 'all',
            sort: search.sort ?? 'priority',
            search: search.search ?? '',
          }}
          onFiltersChange={handleFiltersChange}
          onConversationStarted={(id) =>
            void navigate({
              search: (prev) => ({ ...prev, id }),
              replace: false,
            })
          }
          fetchNextPage={() => void fetchNextPage()}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          totalCount={totalCount}
          channelCounts={channelCounts}
          modeCounts={modeCounts}
          interactionCounts={interactionCounts}
          onOpenBoard={openBoard}
          selectedAttendanceIds={selectedAttendanceIds}
          onAttendanceSelection={handleAttendanceSelection}
          onAllLoadedSelection={handleAllLoadedSelection}
          onBulkFinalize={() => setBulkFinalizationOpen(true)}
          onBulkAction={() => setBulkActionOpen(true)}
          onSelectAllMatching={() => void handleSelectAllMatching()}
          isSelectingAll={resolveBulkSelection.isPending}
          allMatchingSelected={allMatchingSelected}
          canManageBulkActions={
            currentRole === 'OWNER' ||
            currentRole === 'ADMIN' ||
            currentRole === 'MANAGER'
          }
        />
      </aside>

      {/* Chat */}
      <section
        className={cn(
          'border-border bg-card min-h-0 min-w-0 flex-1 flex-col border-l',
          selectedId ? 'flex' : 'hidden md:flex'
        )}
      >
        {activeSelectedId && selectedAttendance ? (
          <InboxChat
            attendance={selectedAttendance}
            messages={messages}
            messagesLoading={messagesLoading}
            currentUserId={user?.id}
            members={members}
            orgId={currentOrgId}
            onBack={() =>
              void navigate({
                search: (prev) => ({ ...prev, id: undefined }),
                replace: false,
              })
            }
            onTransfer={openTransfer}
            onFinalize={() => setFinalizationOpen(true)}
            detailsOpen={detailsOpen}
            onToggleDetails={() => setDetailsOpen((v) => !v)}
            onOpenBoard={openBoard}
            fetchOlderMessages={() => void fetchOlderMessages()}
            hasOlderMessages={hasOlderMessages}
            isFetchingOlderMessages={isFetchingOlderMessages}
          />
        ) : activeSelectedId && selectedLoading ? (
          <InboxChatSkeleton />
        ) : (
          <div className="text-muted-foreground flex flex-1 items-center justify-center">
            <p className="text-sm">Selecione uma conversa para começar</p>
          </div>
        )}
      </section>

      {/* Details */}
      <aside
        className={cn(
          'border-border hidden min-h-0 shrink-0 border-l transition-all duration-200 lg:flex lg:flex-col',
          detailsOpen ? 'lg:w-72' : 'lg:w-0 lg:overflow-hidden lg:border-l-0'
        )}
      >
        <InboxDetails
          attendance={selectedAttendance ?? null}
          members={members}
          isLoading={selectedLoading && !selectedAttendance}
          onTransfer={openTransfer}
          onFinalize={() => setFinalizationOpen(true)}
          onArchive={() => archiveMutation.mutateAsync()}
          onViewLead={(id) => {
            const inboxSearch = selectedId
              ? `?inboxId=${encodeURIComponent(selectedId)}`
              : ''

            window.open(
              `/leads/${encodeURIComponent(id)}${inboxSearch}`,
              '_blank',
              'noopener,noreferrer'
            )
          }}
        />
      </aside>

      {/* Finalization Dialog */}
      {activeSelectedId && (
        <FinalizationDialog
          open={finalizationOpen}
          onOpenChange={setFinalizationOpen}
          orgId={currentOrgId}
          attendanceId={activeSelectedId}
          onFinalized={() => {
            setFinalizationOpen(false)
            // Volta ao estado vazio ("Selecione uma conversa para começar")
            // ao finalizar — não manter o chat finalizado aberto.
            void navigate({
              search: (prev) => ({ ...prev, id: undefined }),
            })
          }}
        />
      )}

      <BulkFinalizationDialog
        open={bulkFinalizationOpen}
        onOpenChange={setBulkFinalizationOpen}
        orgId={currentOrgId}
        attendanceIds={[...selectedAttendanceIds]}
        attendanceLabels={attendanceLabels}
        onFinalized={(result) => {
          setAllMatchingSelected(false)
          setSelectedAttendanceIds(
            new Set(result.failed.map((failure) => failure.attendanceId))
          )
        }}
      />

      <BulkAttendanceActionDialog
        open={bulkActionOpen}
        onOpenChange={setBulkActionOpen}
        orgId={currentOrgId}
        attendanceIds={[...selectedAttendanceIds]}
        attendanceLabels={attendanceLabels}
        members={members}
        tags={tags}
        onCompleted={(result) => {
          setAllMatchingSelected(false)
          setSelectedAttendanceIds(
            new Set(result.failed.map((failure) => failure.attendanceId))
          )
        }}
      />

      {/* Transfer Dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Transferir atendimento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-muted-foreground text-xs font-medium">
                Setor
              </label>
              <Select
                value={transferDeptId}
                onValueChange={(value) => {
                  setTransferDeptId(value)
                  // Limpa o responsável se ele não pertencer ao novo setor.
                  const stillEligible = members.some(
                    (m) =>
                      m.id === transferUserId && m.departmentIds.includes(value)
                  )
                  if (!stillEligible) setTransferUserId('none')
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um setor" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-muted-foreground text-xs font-medium">
                Responsável
              </label>
              <Select value={transferUserId} onValueChange={setTransferUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um membro" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem responsável</SelectItem>
                  {transferEligibleMembers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name ?? m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setTransferOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={transferMutation.isPending}
              onClick={() => void handleTransferConfirm()}
            >
              {transferMutation.isPending ? 'Transferindo...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function InboxChatSkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-3 border-b px-4 py-3">
        <Skeleton className="h-9 w-9 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-end gap-3 p-4">
        <Skeleton className="h-12 w-2/3 rounded-xl" />
        <Skeleton className="ml-auto h-16 w-3/5 rounded-xl" />
        <Skeleton className="h-10 w-1/2 rounded-xl" />
        <Skeleton className="ml-auto h-12 w-2/3 rounded-xl" />
      </div>
      <div className="border-t p-3">
        <Skeleton className="h-11 w-full rounded-lg" />
      </div>
    </div>
  )
}
